import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient } from '../api/client';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'SYSTEM_ADMIN' | 'CO_ADMIN' | 'STORE_ADMIN';
  storeId?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (token: string) => void;
  loginWithCredentials: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setUser: (user: User | null) => void;
}

// Decode JWT payload without verification (verification happens server-side)
function decodeToken(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up unauthorized callback
    apiClient.setOnUnauthorized(() => {
      localStorage.removeItem('accessToken');
      delete apiClient.defaults.headers.common['Authorization'];
      setUser(null);
    });

    // Check for existing token on mount
    const token = localStorage.getItem('accessToken');
    if (token) {
      validateToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const validateToken = async (token: string) => {
    try {
      // First try to decode the token to get user info
      const payload = decodeToken(token);
      if (payload) {
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser({
          id: payload.sub,
          email: payload.email,
          name: payload.name,
          role: payload.role,
          storeId: payload.storeId,
        });
        setLoading(false);
        return;
      }
      // Fallback: validate with server
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await apiClient.get<{ data: User }>('/auth/admin/me');
      setUser(response.data.data);
    } catch (error) {
      localStorage.removeItem('accessToken');
      delete apiClient.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  // Login with a JWT token (for magic link, invite accept, etc.)
  const login = (token: string) => {
    const payload = decodeToken(token);
    if (!payload) {
      console.error('Invalid token');
      return;
    }

    localStorage.setItem('accessToken', token);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser({
      id: payload.sub,
      email: payload.email,
      name: payload.name,
      role: payload.role,
      storeId: payload.storeId,
    });
  };

  // Login with email/password (for admin login)
  const loginWithCredentials = async (email: string, password: string) => {
    const response = await apiClient.post<{ data: { token: string; user: User } }>('/auth/admin/login', { email, password });
    const { token, user: userData } = response.data.data;

    localStorage.setItem('accessToken', token);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    delete apiClient.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithCredentials, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
